//@flow
import * as Immutable from 'immutable';

import { ZoneMapGeometry } from 'common/models/flow/ZoneMapGeometry';
import type { ZoneMapGeometryModelType } from 'common/models/flow/ZoneMapGeometry';

import { LatLngGeometry } from 'common/models/internal/LatLngGeometry';
import { ZoneMapGeometryId } from 'common/models/internal/ZoneMapGeometryId';
import { UserId } from 'common/models/internal/UserId';

import { LatLngInnerPolygon } from 'common/models/internal/LatLngPolygon';
import { ZoneMapGeometryTypeEnum } from 'common/enums/ZoneMapGeometryType';
import type { LatLngModelType } from 'common/models/flow/LatLng';
import type { MapGeometryEnum } from 'common/models/flow/LatLngPolygon';
import { LatLng } from 'common/models/internal/LatLng';
import { ZoneMapVehicleId } from 'common/models/internal/ZoneMapVehicleId';

import { VerificationStatus } from 'common/models/internal/VerificationStatus';
import { ZoneMapGeometryStatusEnum } from 'common/enums/ZoneMapGeometryStatus';

import { VehicleModelId } from './VehicleModelId';

type ZoneMapGeometryInternalStatusEnum = 'HIDDEN' | 'NEW' | 'SEARCH';

class InternalZoneMapGeometry extends ZoneMapGeometry {
    static fromJS(json: ZoneMapGeometryModelType): InternalZoneMapGeometry {
        const state: Object = Object.assign({}, json);
        state.id = ZoneMapGeometryId.fromJS(state.id);
        state.geometry = LatLngGeometry.fromJS(state.geometry);
        state.localities = Immutable.List(state.localities);
        state.apartments = Immutable.List(state.apartments);
        state.pincodes = Immutable.List(state.pincodes);
        state.users = Immutable.List(state.users).map(item =>
            UserId.fromJS(item)
        );
        state.vehicles = Immutable.List(state.vehicles).map(item =>
            ZoneMapVehicleId.fromJS(item)
        );
        state.vehicleModels = Immutable.List(state.vehicleModels).map(item =>
            VehicleModelId.fromJS(item)
        );
        state.verificationStatus = VerificationStatus.fromJS(
            state.verificationStatus
        );
        return new this(Immutable.Map(state));
    }

    setInternalStatus(
        status: ZoneMapGeometryInternalStatusEnum
    ): InternalZoneMapGeometry {
        return this.clone(this._state.set('internalStatus', status));
    }

    getInternalStatus(): ZoneMapGeometryInternalStatusEnum {
        return this._state.get('internalStatus');
    }

    isHidden(): boolean {
        return this.getInternalStatus()
            ? this.getInternalStatus() === ZoneMapGeometryStatusEnum.Hidden
            : false;
    }

    getStyles(): Object {
        return this.visualProperties
            ? { ...this.visualProperties }
            : { color: '#479af0', opacity: 1 };
    }

    setStyles(styles: Object): ZoneMapGeometry {
        const updatedStyles = { ...this.getStyles(), ...styles };
        return this.setVisualProperties(updatedStyles);
    }

    isVerified(): boolean {
        return this.verificationStatus && this.verificationStatus.verified
            ? this.verificationStatus.verified
            : false;
    }

    getType(): MapGeometryEnum {
        const { polygon, circle } = this.geometry;
        if (circle) {
            return ZoneMapGeometryTypeEnum.Circle;
        }

        return polygon && polygon.type;
    }

    getVertices(): Array<LatLngModelType> {
        return (
            this.geometry &&
            this.geometry.polygon &&
            this.geometry.polygon.vertices
        );
    }

    getInnerPolygons(): Array<LatLngInnerPolygon> {
        return (
            this.geometry &&
            this.geometry.polygon &&
            this.geometry.polygon.innerPolygons
        );
    }

    setInnerPolygons(
        polygons: Array<LatLngInnerPolygon>
    ): InternalZoneMapGeometry {
        return polygons
            ? this.geometry &&
                  this.geometry.polygon &&
                  this.setGeometry(
                      this.geometry.setPolygon(
                          this.geometry.polygon.setInnerPolygons(polygons)
                      )
                  )
            : this;
    }

    updateInnerPolygons(
        vertices: Array<LatLngModelType>
    ): InternalZoneMapGeometry {
        const currentPolygons = this.getInnerPolygons();
        const updatedInnerPolygons = currentPolygons.push(
            LatLngInnerPolygon.fromJS({
                vertices
            })
        );
        return this.setInnerPolygons(updatedInnerPolygons);
    }

    updateVertices(vertices: Array<LatLngModelType>): InternalZoneMapGeometry {
        if (this.getType() === ZoneMapGeometryTypeEnum.Circle || !vertices) {
            return this;
        }

        let updatedGeometry = this;
        if (updatedGeometry.geometry && updatedGeometry.geometry.polygon) {
            const transformedVertices = Immutable.List(vertices).map(vertex =>
                LatLng.fromJS(vertex)
            );

            updatedGeometry = updatedGeometry.setGeometry(
                updatedGeometry.geometry.setPolygon(
                    updatedGeometry.geometry.polygon.setVertices(
                        transformedVertices
                    )
                )
            );
        }
        return updatedGeometry;
    }

    updateRadius(radius: number): InternalZoneMapGeometry {
        if (this.getType() !== ZoneMapGeometryTypeEnum.Circle) {
            return this;
        }

        return (
            this.geometry &&
            this.geometry.circle &&
            this.setGeometry(
                this.geometry.setCircle(this.geometry.circle.setRadius(radius))
            )
        );
    }

    get id(): ZoneMapGeometryId {
        return this._state.get('id');
    }

    get geometry(): LatLngGeometry {
        return this._state.get('geometry');
    }

    get users(): Immutable.List<UserId> {
        return this._state.get('users');
    }

    get verificationStatus(): VerificationStatus {
        return this._state.get('verificationStatus');
    }

    get vehicles(): Immutable.List<ZoneMapVehicleId> {
        return this._state.get('vehicles');
    }

    get vehicleModels(): Immutable.List<VehicleModelId> {
        return this._state.get('vehicleModels');
    }

    setVehicleModels(vehicleModels: Immutable.List<VehicleModelId>): this {
        return this.clone(this._state.set('vehicleModels', vehicleModels));
    }
}

export { InternalZoneMapGeometry as ZoneMapGeometry };
